document.addEventListener( 'alpine:init', () => {

	Alpine.data( 'fullBleedVideo', ( videos, playOnLoad, delay ) => ({

		videos,
		playOnLoad,
		delay,
		loadingText: true,

		init() {
			const video = this.$refs.bgVideo;
			if ( ! video ) return;

			const container = video.closest( '.full-bleed-video' );

			let playTimeout            = null;
			let loadingFallbackTimeout = null;
			let posterBlendTimeout     = null;
			let playToken              = 0;
			let hasLoadedSource        = false;
			let hasStartedPlayback     = false;
			let posterFallbackActive   = false;
			let activePoster           = video.getAttribute( 'poster' ) || '';

			const playDelay           = Number.parseInt( this.delay, 10 ) || 0;
			const isIOSDevice         = /iPad|iPhone|iPod/.test( navigator.userAgent ) || ( 'MacIntel' === navigator.platform && 1 < navigator.maxTouchPoints );
			const shouldBypassDelay   = this.playOnLoad && isIOSDevice;
			let effectivePlayDelay = playDelay;
			if ( shouldBypassDelay ) {
				effectivePlayDelay = 0;
			}
			const maxLoadingWait      = Math.max( 2500, effectivePlayDelay + 3000 );
			const shouldDelayAutoplay = this.playOnLoad && 0 < effectivePlayDelay;

			if ( shouldDelayAutoplay ) {
				video.autoplay = false;
				video.pause();
			}

			const clearPlayTimeout = () => {

				if ( playTimeout ) {
					clearTimeout( playTimeout );
					playTimeout = null;
				}
			};

			const clearLoadingFallbackTimeout = () => {

				if ( loadingFallbackTimeout ) {
					clearTimeout( loadingFallbackTimeout );
					loadingFallbackTimeout = null;
				}
			};

			const clearPosterBlendTimeout = () => {

				if ( posterBlendTimeout ) {
					clearTimeout( posterBlendTimeout );
					posterBlendTimeout = null;
				}
			};

			const hideLoadingText = () => {
				if ( ! this.loadingText ) return;
				this.loadingText = false;
				clearLoadingFallbackTimeout();
			};

			const clearPosterFallback = () => {
				if ( ! posterFallbackActive ) return;

				video.style.removeProperty( 'opacity' );
				video.style.removeProperty( 'visibility' );
				posterFallbackActive = false;
			};

			const applyPosterOverlay = () => {
				if ( ! activePoster || ! container || posterFallbackActive ) return;

				container.style.backgroundImage    = `url("${activePoster}")`;
				container.style.backgroundSize     = 'cover';
				container.style.backgroundPosition = 'center';
				container.style.backgroundRepeat   = 'no-repeat';
			};

			const clearPosterOverlay = ( withDelay = false ) => {
				if ( ! container || posterFallbackActive ) return;

				clearPosterBlendTimeout();

				const removeOverlay = () => {
					if ( posterFallbackActive ) return;

					container.style.removeProperty( 'background-image' );
					container.style.removeProperty( 'background-size' );
					container.style.removeProperty( 'background-position' );
					container.style.removeProperty( 'background-repeat' );
				};

				if ( withDelay ) {
					posterBlendTimeout = setTimeout( () => {
						posterBlendTimeout = null;
						removeOverlay();
					}, 180 );
					return;
				}

				removeOverlay();
			};

			const applyPosterFallback = () => {
				if ( ! activePoster ) return;

				if ( container ) {
					container.style.backgroundImage    = `url("${activePoster}")`;
					container.style.backgroundSize     = 'cover';
					container.style.backgroundPosition = 'center';
					container.style.backgroundRepeat   = 'no-repeat';
				}

				video.pause();
				video.style.opacity    = '0';
				video.style.visibility = 'hidden';
				clearPosterBlendTimeout();
				posterFallbackActive = true;
			};

			const queueLoadingFallback = () => {
				if ( ! this.playOnLoad || ! this.loadingText ) return;

				clearLoadingFallbackTimeout();
				loadingFallbackTimeout = setTimeout( () => {
					if ( ! hasStartedPlayback ) {
						applyPosterFallback();
					}

					hideLoadingText();
				}, maxLoadingWait );
			};

			const safePlay = token => {
				if ( token !== playToken ) return;

				const runPlay = () => {
					if ( token !== playToken ) return;

					video.autoplay = true;
					queueLoadingFallback();
					const playPromise = video.play();

					if ( playPromise && 'function' === typeof playPromise.catch ) {
						playPromise
							.then( () => {
								if ( token === playToken ) {
									hasStartedPlayback = true;
									clearPosterFallback();
									hideLoadingText();
								}
							})
							.catch( error => {
								if ( 'AbortError' === error.name ) return;

								applyPosterFallback();

								hideLoadingText();
							});
					} else {
						hasStartedPlayback = true;
						clearPosterFallback();
						hideLoadingText();
					}
				};

				if ( 2 <= video.readyState ) {
					runPlay();
					return;
				}

				video.addEventListener( 'canplay', runPlay, { once: true });
			};

			const playWithDelay = () => {
				clearPlayTimeout();
				playToken += 1;
				const token = playToken;

				if ( 0 < effectivePlayDelay ) {
					playTimeout = setTimeout( () => {
						playTimeout = null;
						safePlay( token );
					}, effectivePlayDelay );
					return;
				}

				safePlay( token );
			};

			const hasUsableSources = orientation => {
				let set = this.videos[orientation];
				if ( ! set ) return false;
				return Boolean( set.webm ) || Boolean( set.mp4 );
			};

			const getOrientation = () => {
				if ( window.matchMedia ) {
					if ( window.matchMedia( '( orientation: portrait )' ).matches ) {
						return 'portrait';
					}
					return 'landscape';
				}

				if ( screen.orientation ) {
					if ( screen.orientation.type.startsWith( 'portrait' ) ) {
						return 'portrait';
					}
					return 'landscape';
				}

				return 'landscape';
			};

			const resolveOrientation = requestedOrientation => {
				if ( hasUsableSources( requestedOrientation ) ) {
					return requestedOrientation;
				}

				let opposite = 'portrait';
				if ( 'portrait' === requestedOrientation ) {
					opposite = 'landscape';
				}
				if ( hasUsableSources( opposite ) ) {
					return opposite;
				}

				return null;
			};

			const swapVideo = orientation => {
				const resolvedOrientation = resolveOrientation( orientation );
				if ( ! resolvedOrientation ) {
					applyPosterFallback();
					hideLoadingText();
					return;
				}

				const videoSet = this.videos[resolvedOrientation];
				if ( ! videoSet ) return;

				const { webm, mp4, poster } = videoSet;

				if ( video.dataset.orientation === resolvedOrientation ) return;
				video.dataset.orientation = resolvedOrientation;

				if ( poster ) {
					activePoster = poster;
					video.poster = poster;
					applyPosterOverlay();

					if ( posterFallbackActive ) {
						applyPosterFallback();
					}
				}

				const webmSource = video.querySelector( 'source[type="video/webm"]' );
				const mp4Source = video.querySelector( 'source[type="video/mp4"]' );

				if ( webmSource ) {
					if ( webm ) {
						webmSource.src = webm;
					}
					webmSource.removeAttribute( 'media' );
				}

				if ( mp4Source ) {
					if ( mp4 ) {
						mp4Source.src = mp4;
					}
					mp4Source.removeAttribute( 'media' );
				}

				if ( ! webm && ! mp4 ) {
					applyPosterFallback();
					hideLoadingText();
					return;
				}

				hasLoadedSource = true;
				video.load();

				if ( this.playOnLoad || 'true' === video.dataset.inViewport ) {
					playWithDelay();
				}
			};

			const syncOrientation = () => {
				if ( this.playOnLoad || hasLoadedSource ) {
					swapVideo( getOrientation() );
				}
			};

			if ( window.matchMedia ) {
				const orientationMql = window.matchMedia( '( orientation: portrait )' );
				if ( orientationMql.addEventListener ) {
					orientationMql.addEventListener( 'change', syncOrientation );
				} else if ( orientationMql.addListener ) {
					orientationMql.addListener( syncOrientation );
				}
			}

			video.addEventListener( 'playing', () => {
				hasStartedPlayback = true;
				clearPosterFallback();
				clearPosterOverlay( true );
				hideLoadingText();
			});
			video.addEventListener( 'loadeddata', () => {
				applyPosterOverlay();
			});
			video.addEventListener( 'error', () => {
				if ( ! hasStartedPlayback ) {
					applyPosterFallback();
				}

				hideLoadingText();
			});
			video.addEventListener( 'abort', queueLoadingFallback );
			video.addEventListener( 'stalled', () => {
				applyPosterOverlay();
				queueLoadingFallback();
			});

			queueLoadingFallback();

			if ( this.playOnLoad ) {

				requestAnimationFrame( () => {

					swapVideo( getOrientation() );
				});
			} else {

				const observer = new IntersectionObserver( entries => {

					entries.forEach( entry => {

						if ( entry.isIntersecting ) {

							video.dataset.inViewport = 'true';
							playWithDelay();
						} else {
							video.dataset.inViewport = 'false';
							clearPlayTimeout();
							playToken += 1;
							video.pause();
						}
					});
				}, {
					rootMargin: '250px',
				});

				const loadObserver = new IntersectionObserver( entries => {

					entries.forEach( entry => {

						if ( entry.isIntersecting ) {

							swapVideo( getOrientation() );
							loadObserver.unobserve( video );
						}
					});
				}, {
					rootMargin: '250px',
				});

				loadObserver.observe( video );
				observer.observe( video );
			}
		},
	}) );
});
